import type { MeResponse } from "@salon-pos/shared";

/**
 * API client. Works same-origin in the browser (httpOnly cookie + CSRF header) and from a
 * Capacitor bundle (capacitor://localhost on iOS, https://localhost on Android) against a remote
 * VITE_API_BASE_URL using a bearer session token.
 */
let BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
export const CLIENT: "web" | "native" = import.meta.env.VITE_CLIENT === "native" ? "native" : "web";
/** The API origin ("" = same origin). The native app may change it at runtime (hidden Server setting). */
export const apiBase = () => BASE;
export function setApiBase(url: string) { BASE = url.replace(/\/$/, ""); }
/** Absolute URL for API-served files (images, receipts) — needed when the app is not served by the API. */
export const apiUrl = (path: string) => (/^(https?:|data:|blob:)/.test(path) ? path : BASE + path);

/**
 * Connectivity, kept current by the native shell (Capacitor Network) or browser online/offline events.
 * Money is never queued: while offline, payment requests are refused before they are sent.
 */
let online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
const onlineListeners = new Set<(v: boolean) => void>();
export const isOnline = () => online;
export function setOnline(v: boolean) { if (v === online) return; online = v; onlineListeners.forEach((f) => f(v)); }
export function onOnlineChange(f: (v: boolean) => void) { onlineListeners.add(f); return () => { onlineListeners.delete(f); }; }
if (typeof window !== "undefined") {
  window.addEventListener("online", () => setOnline(true));
  window.addEventListener("offline", () => setOnline(false));
}
/** Requests that move money (take payment, online payment links, refunds, top-ups paid at the till). */
export function isPaymentRequest(method: string, path: string): boolean {
  if (method === "GET") return false;
  const p = path.split("?")[0]!;
  return /\/api\/orders\/[^/]+\/(payments|refunds|refund-requests|complete)$/.test(p) || /\/api\/appointments\/[^/]+\/(deposit|no-show-fee)$/.test(p)
    || /\/api\/refund-requests\/[^/]+\/approve$/.test(p) || /\/api\/customer-packages\/[^/]+\/refund$/.test(p) || /\/api\/customers\/[^/]+\/credit\/adjust$/.test(p);
}

/** Token storage for native builds. In-memory by default; the Capacitor shell will plug in Keychain/Keystore. */
export interface TokenStore { get(): Promise<string | null>; set(token: string | null): Promise<void> }
let memoryToken: string | null = null;
export let tokenStore: TokenStore = { get: async () => memoryToken, set: async (t) => { memoryToken = t; } };
export function setTokenStore(store: TokenStore) { tokenStore = store; }

let csrfToken = "";
export function setCsrf(token: string) { csrfToken = token; }
/** (Task Q) for raw uploads that don't go through api() */
export const csrfHeader = (): Record<string, string> => (csrfToken ? { "x-csrf-token": csrfToken } : {});

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: Record<string, any>) { super(message); }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json", ...(init.headers ?? {}) };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  if (CLIENT === "native") {
    const tok = await tokenStore.get();
    if (tok) headers.authorization = `Bearer ${tok}`;
  } else if (method !== "GET" && csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }
  // never queue or "retry later" a payment: refuse it up front while offline
  if (!online && isPaymentRequest(method, path)) throw new ApiError(0, "offline_payment", "offline_payment");
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method, headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      credentials: CLIENT === "native" ? "omit" : "include",
    });
  } catch {
    throw new ApiError(0, "network", "network");
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.status === 423 && typeof window !== "undefined") window.dispatchEvent(new Event("spos:kiosk-locked"));
  // (2026-10-10) the e-mail gate started (e.g. another tab changed state): the app reloads /me and shows the gate
  if (res.status === 403 && data?.error?.code === "email_unverified" && typeof window !== "undefined") window.dispatchEvent(new Event("spos:email-unverified"));
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? res.statusText, data?.error?.details);
  return data as T;
}

/** (Security 1) second sign-in step: set up an authenticator app (first time, platform admins) or enter its 6-digit code */
export interface TwoFactorStep { mode: "setup" | "verify"; challenge: string; expiresInSeconds: number; setup?: { secret: string; otpauthUrl: string; qrSvg: string } }

/** Password sign-in: the session (me), or the two-factor step when the account needs one (no session yet). */
export async function login(email: string, password: string): Promise<{ me: MeResponse; twoFactor?: undefined } | { me?: undefined; twoFactor: TwoFactorStep }> {
  const r = await api<(MeResponse & { sessionToken?: string; twoFactor?: undefined }) | { twoFactor: TwoFactorStep }>("/api/auth/login", {
    method: "POST",
    body: { email, password, client: CLIENT, deviceLabel: CLIENT === "native" ? navigator.userAgent.slice(0, 100) : undefined },
  });
  if (r.twoFactor) return { twoFactor: r.twoFactor };
  const me = r as MeResponse & { sessionToken?: string };
  if (me.sessionToken) await tokenStore.set(me.sessionToken);
  setCsrf(me.csrfToken);
  return { me };
}

/** Finish the two-factor step (6-digit code or a recovery code). The first set-up also returns the recovery codes (shown once). */
export async function verifyTwoFactor(challenge: string, code: string): Promise<{ me: MeResponse; recoveryCodes?: string[] }> {
  const r = await api<MeResponse & { sessionToken?: string; recoveryCodes?: string[] }>("/api/auth/2fa/verify", { method: "POST", body: { challenge, code } });
  if (r.sessionToken) await tokenStore.set(r.sessionToken);
  setCsrf(r.csrfToken);
  const { recoveryCodes, sessionToken: _t, ...me } = r;
  return { me: me as MeResponse, recoveryCodes };
}

/** Run before the session ends (e.g. the native app forgets this device's push token while still signed in). */
const logoutHooks: Array<() => Promise<void>> = [];
export function addLogoutHook(h: () => Promise<void>) { logoutHooks.push(h); }

export async function logout() {
  for (const h of logoutHooks) await h().catch(() => undefined);
  await api("/api/auth/logout", { method: "POST" }).catch(() => {});
  await tokenStore.set(null);
  setCsrf("");
}

/** Text/HTML from the API with the same auth as api() (receipt previews in the native app). */
export async function apiText(path: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (CLIENT === "native") { const tok = await tokenStore.get(); if (tok) headers.authorization = "Bearer " + tok; }
  const res = await fetch(BASE + path, { headers, credentials: CLIENT === "native" ? "omit" : "include" });
  if (!res.ok) throw new ApiError(res.status, "error", res.statusText);
  return res.text();
}

/** Binary from the API (authenticated images in the native app, where <img> can't send the bearer token). */
export async function apiBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (CLIENT === "native") { const tok = await tokenStore.get(); if (tok) headers.authorization = "Bearer " + tok; }
  const res = await fetch(apiUrl(path), { headers, credentials: CLIENT === "native" ? "omit" : "include" });
  if (!res.ok) throw new ApiError(res.status, "error", res.statusText);
  return res.blob();
}
